import React, { useState } from 'react';
import {
  COLLABORATION_TRANSLATION_LANGUAGES,
  translationLanguageLabel,
  type CollaborationThreadMessage
} from './collaborationThreadShared';

/**
 * Tracker #57 — per-message translation controls for the supplier quality
 * thread. Stored translations render beneath the original, each labelled
 * with its language, provider and time; the original is always shown and
 * never replaced. Translating again into the same language replaces that
 * language's rendering only.
 */
interface CollaborationMessageTranslationsProps {
  message: CollaborationThreadMessage;
  translatingMessageId: string;
  relativeTime: (value: string) => string;
  /** `force` is true when a current rendering exists and the user asked to translate again (meters again). */
  onTranslate: (messageId: string, targetLanguage: string, force?: boolean) => void;
  /** Hidden entirely when the caller knows translation is unavailable. */
  available?: boolean;
}

const providerLabel = (provider: string): string =>
  provider === 'azure_translator' ? 'Azure AI Translator' : provider || 'machine translation';

const CollaborationMessageTranslations = ({
  message,
  translatingMessageId,
  relativeTime,
  onTranslate,
  available = true
}: CollaborationMessageTranslationsProps): JSX.Element | null => {
  const [targetLanguage, setTargetLanguage] = useState<string>(COLLABORATION_TRANSLATION_LANGUAGES[0].code);
  if (message.messageType !== 'dialogue') {
    return null;
  }
  const translations = message.translations ?? [];
  const translating = translatingMessageId === message.messageId;
  const hasCurrentRendering = translations.some((entry) => entry.targetLanguage === targetLanguage && !entry.sourceOutdated);

  return (
    <div className="collaboration-message-translations" style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
      {translations.map((translation) => (
        <div
          key={translation.translationId}
          className="collaboration-message-translation"
          style={{ borderLeft: '3px solid var(--color-border, #d0d4dc)', paddingLeft: '10px' }}
          lang={translation.targetLanguage}
        >
          <div className="muted" style={{ fontSize: '0.85em' }}>
            Translated to {translationLanguageLabel(translation.targetLanguage)}
            {translation.sourceLanguage ? ` from ${translationLanguageLabel(translation.sourceLanguage)}` : ''}
            {' · '}{providerLabel(translation.provider)}
            {' · '}{relativeTime(translation.createdAtUtc)}
            {translation.sourceOutdated && (
              <span className="badge-subtle" style={{ marginLeft: '6px' }}>Original edited since</span>
            )}
          </div>
          <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{translation.translatedBody}</div>
        </div>
      ))}
      {available && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="muted" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span>Translate to</span>
            <select
              className="input"
              value={targetLanguage}
              onChange={(event) => setTargetLanguage(event.target.value)}
              disabled={translating}
              aria-label="Translation target language"
            >
              {COLLABORATION_TRANSLATION_LANGUAGES.map((language) => (
                <option key={language.code} value={language.code}>{language.label}</option>
              ))}
            </select>
          </label>
          <button
            className="btn secondary"
            type="button"
            onClick={() => onTranslate(message.messageId, targetLanguage, hasCurrentRendering)}
            disabled={translating}
          >
            {translating ? 'Translating…' : hasCurrentRendering ? 'Translate again' : 'Translate'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CollaborationMessageTranslations;
